// --- START OF FILE PracticeView.js ---

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, BarChart2, Clock } from 'lucide-react';

// --- Helper Components (copied from StatsView for self-containment) ---

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
    // A simplified, always-open version for this view
    return (
        <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
            <h3 className="font-bold text-lg mb-4 flex items-center">
                {Icon && <Icon size={20} className="mr-2" />} {title}
            </h3>
            {children}
        </div>
    );
};

const StatCard = ({ title, value, unit = '' }) => (
    <motion.div
        key={`${title}-${value}`}
        initial={{ scale: 0.95, opacity: 0.8 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className="bg-slate-700/50 p-3 rounded-lg text-center"
    >
        <div className="text-sm text-slate-400 mb-1 truncate">{title}</div>
        <div className="text-2xl font-bold text-white">{value}<span className="text-lg text-slate-300">{unit}</span></div>
    </motion.div>
);

const formatDate = (timestamp) => {
    if (timestamp && typeof timestamp.toDate === 'function') return new Date(timestamp.toDate()).toLocaleDateString();
    if (timestamp) return new Date(timestamp).toLocaleDateString();
    return 'N/A';
};

const calculatePracticeStats = (matchHistory, currentUserName) => {
    const initialStats = {
        gamesWon: 0,
        gamesLost: 0,
        winPercentage: '0.0',
        totalShots: 0,
        pottingPercentage: '0.0',
        breakAndRuns: 0,
    };

    if (!matchHistory || matchHistory.length === 0) {
        return initialStats;
    }

    let successfulShots = 0;

    for (const match of matchHistory) {
        const playerHandle = match.matchData.player1Id === currentUserName ? 'player1' : 'player2';
        if (match.gameState.winner === playerHandle) {
            initialStats.gamesWon++;
        } else {
            initialStats.gamesLost++;
        }

        for (const shot of match.shotLog) {
            if (shot.player === playerHandle) {
                initialStats.totalShots++;
                if (shot.result === 'made') {
                    successfulShots++;
                }
                if (shot.tags.includes('break_and_run')) {
                    initialStats.breakAndRuns++;
                }
            }
        }
    }

    initialStats.winPercentage = (initialStats.gamesWon + initialStats.gamesLost) > 0 
        ? ((initialStats.gamesWon / (initialStats.gamesWon + initialStats.gamesLost)) * 100).toFixed(1) 
        : '0.0';
    
    initialStats.pottingPercentage = initialStats.totalShots > 0 
        ? ((successfulShots / initialStats.totalShots) * 100).toFixed(1) 
        : '0.0';

    return initialStats;
};


export const PracticeView = ({ matchHistory, currentUserData, onBack, onReviewMatch }) => {
    const practiceMatches = useMemo(() => matchHistory.filter(m => m.matchData.player2Id === 'ghost'), [matchHistory]);
    const stats = useMemo(() => calculatePracticeStats(practiceMatches, currentUserData.uid), [practiceMatches, currentUserData.uid]);

    return (
        <div className="p-2 sm:p-4 max-w-4xl mx-auto pb-8">
            <div className="flex items-center mb-6">
                <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-700 -ml-2">
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <h1 className="text-2xl sm:text-3xl font-bold ml-4">Practice Dashboard</h1>
            </div>

            {practiceMatches.length > 0 ? (
                <div className="space-y-6">
                    <Section title="Practice vs. Ghost" icon={Sparkles}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <StatCard title="Win Rate" value={stats.winPercentage} unit="%" />
                            <StatCard title="Practice W-L" value={`${stats.gamesWon} - ${stats.gamesLost}`} />
                            <StatCard title="Total Sessions" value={practiceMatches.length} />
                            <StatCard title="Potting %" value={stats.pottingPercentage} unit="%" />
                        </div>
                    </Section>
                    
                    <Section title="Practice History" icon={BarChart2}>
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                            {practiceMatches.map(match => (
                                <button 
                                    key={match.id} 
                                    onClick={() => onReviewMatch(match)}
                                    disabled={match.matchData.trackingMode === 'simple'}
                                    className={`w-full text-left bg-slate-700/50 p-3 rounded-lg transition-colors ${match.matchData.trackingMode === 'simple' ? 'cursor-not-allowed opacity-70' : 'hover:bg-slate-700/70'}`}
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="font-bold">{match.matchData.gameType}</p>
                                            <p className="text-xs text-slate-400">Race to {match.matchData.player1RaceTo}</p>
                                        </div>
                                        <div className="font-semibold text-lg">
                                            {match.gameState.player1Score} / {match.matchData.player1RaceTo}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                            <Clock size={12}/>
                                            <span>{formatDate(match.completedAt || match.gameState.endTime)}</span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Section>
                </div>
            ) : (
                <div className="text-center bg-slate-800/50 p-8 rounded-xl border border-slate-700 mt-8">
                    <h3 className="text-xl font-bold">No Practice Data Found</h3>
                    <p className="text-slate-400 mt-2">Complete some "Player vs. Ghost" matches to see your practice stats here.</p>
                </div>
            )}
        </div>
    );
};